"use client";

import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "./auth";
import { supabase } from "./supabase";
import { deriveFetchErrorClass } from "./fetch-error-class";
import { deriveDeviceClass } from "./device-hint";

// Module-level ref so emitSignal() works outside React
let _userId = null;

// Browser fingerprint — auto-appended to every client-side signal
function getClientContext() {
  if (typeof window === "undefined") return {};
  return {
    url: window.location.pathname + window.location.search,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    connection: navigator.connection?.effectiveType || null,
    downlink: navigator.connection?.downlink || null,
    visibility: document.visibilityState,
    memory: performance.memory?.usedJSHeapSize ? Math.round(performance.memory.usedJSHeapSize / 1048576) : null,
  };
}

/**
 * useSignal() — fire-and-forget signal emission for error/frustration tracking.
 * Inserts into `user_events` with `signal:` prefix. Never blocks UI.
 * Auto-appends browser context to every signal.
 */
// Circuit breaker: after 3 consecutive Supabase failures, stop trying
// and queue to localStorage. Resets on success.
let _consecutiveFailures = 0;
const MAX_FAILURES = 3;

function persistSignal(uid, signal, severity, detail) {
  const payload = {
    user_id: uid,
    event: `signal:${signal}`,
    page: typeof window !== "undefined" ? window.location.pathname : null,
    meta: { severity, ...detail, _client: getClientContext() },
  };

  // Always log warnings/errors to console as fallback
  if (severity === "error") console.error(`[signal:${signal}]`, detail);
  else if (severity === "warning") console.warn(`[signal:${signal}]`, detail);

  // Circuit breaker: queue locally if Supabase is failing
  if (_consecutiveFailures >= MAX_FAILURES) {
    queueSignal(payload);
    return;
  }

  supabase
    .from("user_events")
    .insert(payload)
    .then(() => { _consecutiveFailures = 0; })
    .catch(() => {
      _consecutiveFailures++;
      queueSignal(payload);
    });
}

function queueSignal(payload) {
  try {
    const queue = JSON.parse(localStorage.getItem("fulkit-signal-queue") || "[]");
    queue.push({ ...payload, _queued: Date.now() });
    // Keep queue bounded (max 50)
    if (queue.length > 50) queue.splice(0, queue.length - 50);
    localStorage.setItem("fulkit-signal-queue", JSON.stringify(queue));
  } catch {}
}

// Flush queued signals when connection is restored
export function flushSignalQueue() {
  try {
    const queue = JSON.parse(localStorage.getItem("fulkit-signal-queue") || "[]");
    if (queue.length === 0) return;
    localStorage.removeItem("fulkit-signal-queue");
    for (const payload of queue) {
      supabase.from("user_events").insert(payload).then(() => {}).catch(() => {});
    }
    _consecutiveFailures = 0;
  } catch {}
}

export function useSignal() {
  const { user } = useAuth();

  return useCallback(
    (signal, severity = "info", detail = {}) => {
      const uid = user?.id;
      if (!uid) return;
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        if (params.get("auth")) return;
      }
      persistSignal(uid, signal, severity, detail);
    },
    [user?.id]
  );
}

/**
 * emitSignal() — standalone signal emission for non-React contexts.
 * Uses module-level userId set by SignalCollector.
 */
export function emitSignal(signal, severity = "info", detail = {}) {
  if (!_userId) return;
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth")) return;
  }
  persistSignal(_userId, signal, severity, detail);
}

/**
 * SignalCollector — mount once in layout.js.
 * Captures global JS errors, unhandled rejections, offline/online,
 * quick reloads, and rage clicks. Renders nothing.
 */
export function SignalCollector() {
  const { user } = useAuth();
  const rageRef = useRef([]);

  // Keep module-level userId in sync
  useEffect(() => {
    _userId = user?.id || null;
    return () => { _userId = null; };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    // Skip in dev mode
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth")) return;

    // ── Global JS errors ──
    // Stack capture widened from 3 → 10 frames so the Radio shows
    // enough of the call chain to find the real culprit (Session 46
    // tune-up: the `isApp` regression was buried 5 frames deep and
    // got truncated in the initial pass).
    const onError = (event) => {
      emitSignal("js_error", "error", {
        message: event.message || "Unknown error",
        source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined,
        stack: event.error?.stack?.split("\n").slice(0, 10).join(" | ") || null,
      });
      // Also log to console so dev-tools shows the original error object
      // with its real stack, not just the summarized signal payload.
      try { console.error("[client-error]", event.error || event.message); } catch {}
    };

    // ── Unhandled promise rejections ──
    const onRejection = (event) => {
      const reason = event.reason;
      emitSignal("promise_rejection", "error", {
        message: reason?.message || String(reason || "Unknown rejection"),
        stack: reason?.stack?.split("\n").slice(0, 10).join(" | ") || null,
      });
      try { console.error("[unhandled-rejection]", reason); } catch {}
    };

    // ── Offline / Online ──
    const onOffline = () => emitSignal("offline", "info");
    const onOnline = () => {
      emitSignal("reconnect", "info");
      flushSignalQueue(); // flush any signals queued during offline
    };

    // ── Quick reload detection ──
    const nav = performance.getEntriesByType?.("navigation")?.[0];
    if (nav?.type === "reload") {
      const prevTimestamp = sessionStorage.getItem("fulkit-page-ts");
      if (prevTimestamp) {
        const elapsed = Date.now() - Number(prevTimestamp);
        if (elapsed < 10000) {
          emitSignal("quick_reload", "info", { elapsed });
        }
      }
    }
    sessionStorage.setItem("fulkit-page-ts", String(Date.now()));

    // ── Rage click detection (beta: any same-element rapid clicks) ──
    let lastClickTarget = null;
    const onClick = (e) => {
      const target = e.target;
      if (!target) return;
      const identifier = target.tagName + (target.id ? `#${target.id}` : "") + (target.className?.toString?.().slice(0, 40) || "");
      const now = Date.now();
      if (identifier !== lastClickTarget) {
        rageRef.current = [];
        lastClickTarget = identifier;
      }
      rageRef.current.push(now);
      rageRef.current = rageRef.current.filter((t) => now - t < 2000);
      if (rageRef.current.length >= 3) {
        const isDisabled = target.disabled || target.getAttribute("aria-disabled") === "true" || target.closest("[disabled]");
        emitSignal("rage_click", "info", {
          clicks: rageRef.current.length,
          target: identifier.slice(0, 80),
          disabled: isDisabled || false,
          page: window.location.pathname,
        });
        rageRef.current = [];
      }
    };

    // ── Long tasks (UI jank > 500ms) ──
    let longTaskObserver = null;
    if (typeof PerformanceObserver !== "undefined") {
      try {
        longTaskObserver = new PerformanceObserver((list) => {
          // #128 fix-cluster row #140 (2026-05-05): skip emit when tab hidden.
          // Browser PerformanceLongTaskTiming counter inflates while tab
          // backgrounded (real example: 1364160ms = 22 minutes); not real
          // user-facing UI jank. Filter at observer callback.
          if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
          for (const entry of list.getEntries()) {
            if (entry.duration > 500) {
              emitSignal("long_task", "warning", {
                duration: Math.round(entry.duration),
                page: window.location.pathname,
                device_class: deriveDeviceClass(window.innerWidth, navigator.userAgent),
              });
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ["longtask"] });
      } catch {}
    }

    // ── Tab bounce (left within 10s of arriving) ──
    const arrivalTime = Date.now();
    let bounceReported = false;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden" && !bounceReported) {
        const timeOnPage = Date.now() - arrivalTime;
        if (timeOnPage < 10000) {
          emitSignal("tab_bounce", "info", {
            timeOnPage,
            page: window.location.pathname,
          });
          bounceReported = true;
        }
      }
    };

    // ── Slow page load (> 3s to interactive) ──
    try {
      const navEntry = performance.getEntriesByType?.("navigation")?.[0];
      if (navEntry?.domInteractive && navEntry.domInteractive > 6000) {
        emitSignal("slow_page_load", "warning", {
          domInteractive: Math.round(navEntry.domInteractive),
          loadComplete: Math.round(navEntry.loadEventEnd || 0),
          page: window.location.pathname,
        });
      }
    } catch {}

    // ── Fetch interceptor (catch all 5xx responses) ──
    // Suppressed when navigator reports offline — every fetch fails on a
    // network drop, and a flood of fetch_error signals tells us nothing
    // we don't already know from the OS-level offline indicator.
    // Also suppressed when document.visibilityState === "hidden" — backgrounded
    // tabs see throttled / aborted fetches that look identical to network errors
    // (no HTTP status, "Failed to fetch"). 2026-05 diagnostic: 14/15 false-positive
    // fetch_errors on /settings/owner/users had visibility:"hidden" in _client.
    const origFetch = window.fetch;
    const isSuppressed = () =>
      navigator.onLine === false ||
      (typeof document !== "undefined" && document.visibilityState === "hidden");
    window.fetch = async (...args) => {
      const start = performance.now();
      try {
        const res = await origFetch(...args);
        if (res.status >= 500 && !isSuppressed()) {
          const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
          emitSignal("fetch_error", "error", {
            url: url.split("?")[0], // strip query params
            status: res.status,
            page: window.location.pathname,
            error_class: deriveFetchErrorClass({ status: res.status }),
            elapsed_ms: Math.round(performance.now() - start),
          });
        }
        return res;
      } catch (err) {
        if (!isSuppressed()) {
          const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
          emitSignal("fetch_error", "error", {
            url: url.split("?")[0],
            message: err.message,
            page: window.location.pathname,
            error_class: deriveFetchErrorClass({ errorName: err.name }),
            elapsed_ms: Math.round(performance.now() - start),
          });
        }
        throw err;
      }
    };

    // ── Core Web Vitals (emit only when bad) ──
    let lcpObserver = null;
    let clsObserver = null;
    if (typeof PerformanceObserver !== "undefined") {
      // LCP observer — emits two distinct signals:
      //   slow_lcp (warning)  — fires only when LCP > 6s (legacy threshold-trigger)
      //   lcp_measurement (info) — always-on; fires on every valid LCP entry for
      //     per-page p75 distribution against unified 2.5s SLO (per slo-catalog
      //     sub-tick #1; landed via stations infrastructure ship 2026-05-02
      //     resolving findings-queue row #66). 60s upper bound on both filters
      //     out tab-hidden artifacts per row #65 standing rule.
      // #128 fix-cluster row #141 (2026-05-05): track latest LCP candidate;
      // emit lcp_measurement once per pageview at lifecycle boundary
      // (pagehide + visibilitychange-to-hidden) per Web Vitals official
      // guidance. Multi-fire per page-view inflated histogram counts and
      // skewed p75 distribution against unified 2.5s SLO. slow_lcp warning
      // stays inline (threshold-trigger semantics preserved). Next.js
      // client-side route changes don't fire pagehide → V1.1 follow-on
      // candidate if SPA-nav data loss proves operational.
      let lastLcpEntry = null;
      try {
        lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lcp = entries[entries.length - 1];
          if (!lcp || lcp.startTime <= 0 || lcp.startTime >= 60000) return;
          // Track latest candidate; lcp_measurement deferred to pagehide.
          lastLcpEntry = lcp;
          // Threshold-trigger slow_lcp warning fires inline (legacy semantics).
          if (lcp.startTime > 6000) {
            emitSignal("slow_lcp", "warning", {
              lcp: Math.round(lcp.startTime),
              page: window.location.pathname,
              device_class: deriveDeviceClass(window.innerWidth, navigator.userAgent),
            });
          }
        });
        lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
      } catch {}

      // Emit final lcp_measurement on pageview lifecycle boundary. lcpEmitted
      // flag prevents duplicate emit (visibilitychange-hidden + pagehide both
      // fire on tab close).
      let lcpEmitted = false;
      var emitFinalLcp = () => {
        if (lcpEmitted || !lastLcpEntry) return;
        lcpEmitted = true;
        emitSignal("lcp_measurement", "info", {
          lcp: Math.round(lastLcpEntry.startTime),
          page: window.location.pathname,
          device_class: deriveDeviceClass(window.innerWidth, navigator.userAgent),
        });
      };
      var onLcpPageHide = () => emitFinalLcp();
      var onLcpVisibilityHidden = () => {
        if (document.visibilityState === "hidden") emitFinalLcp();
      };
      window.addEventListener("pagehide", onLcpPageHide);
      document.addEventListener("visibilitychange", onLcpVisibilityHidden);

      // CLS > 0.25 = layout shift problem
      // #69 verification finding (2026-05-07): emit-once per pageview after
      // threshold crossed. Pre-fix the observer re-emitted on every subsequent
      // layout-shift entry while clsValue stayed >0.25 (viewport-resize during
      // CLS measurement triggered ~26 firings/24h on a single pageview). Same
      // dedupe shape as #142 vault_observer + #141 LCP lifecycle pattern.
      try {
        let clsValue = 0;
        let clsEmitted = false;
        clsObserver = new PerformanceObserver((list) => {
          if (clsEmitted) return;
          // #128 fix-cluster Q2 sibling extension (2026-05-05): skip emit when
          // tab hidden. Same pattern as long_task — backgrounded tabs can
          // generate spurious layout-shift entries that aren't user-facing.
          // Sibling-discipline-by-design at fix-time (Phase C pre-flight catch
          // per `feedback_audit_completeness_discipline.md` 11th-instance).
          if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) clsValue += entry.value;
          }
          if (clsValue > 0.25) {
            clsEmitted = true;
            emitSignal("high_cls", "warning", {
              cls: clsValue.toFixed(3),
              page: window.location.pathname,
              device_class: deriveDeviceClass(window.innerWidth, navigator.userAgent),
            });
          }
        });
        clsObserver.observe({ type: "layout-shift", buffered: true });
      } catch {}
    }

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    document.addEventListener("click", onClick, true);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      // #141 LCP lifecycle listeners
      if (typeof onLcpPageHide !== "undefined") window.removeEventListener("pagehide", onLcpPageHide);
      if (typeof onLcpVisibilityHidden !== "undefined") document.removeEventListener("visibilitychange", onLcpVisibilityHidden);
      if (longTaskObserver) longTaskObserver.disconnect();
      if (lcpObserver) lcpObserver.disconnect();
      if (clsObserver) clsObserver.disconnect();
      window.fetch = origFetch;
    };
  }, [user?.id]);

  return null;
}
